import { useQuery, useMutation } from '@tanstack/vue-query'
import { httpRequest } from '@shared/api/httpRequests'
import { urlSubst } from '@shared/utils/url'

/**
 * @constant {string} BASE_URL Base URL path
 */
const BASE_URL = '/deadline/api/v1'

/**
 * @constant {Object.<string:string>} API Map of API CRUD methods and their relative URLs (not including BASE_URL)
 */
export const API = {
  DELETE_DEFINITION: '/delete/:deadlineDefinitionId',
  DISABLE_DEFINITION: '/disable/:deadlineDefinitionId',
  ENABLE_DEFINITION: '/enable/:deadlineDefinitionId',
  GET_DEFINITIONS: '/definitions',
  GET_HISTORY: '/audit/deadline-definitions',
  GET_INSTANCES: '/instances',
  GET_PROJECT_DEADLINES: '/instances/:projectId',
  PAUSE_DEADLINE: '/instance/pause/:deadlineId',
  RESUME_DEADLINE: '/instance/resume/:deadlineId',
  UPDATE_DUE_DATE: '/instance/update/endDate/:deadlineId',
}

/**
 * @constant {Object.<string:string>} DEADLINE_API Map of API CRUD methods and their URLs (including BASE_URL)
 */
export const DEADLINE_API = Object.fromEntries(
  Object.entries(API).map(([key, val]) => [key, `${BASE_URL}${val}`]),
)

export const enableDeadlineDefinition = async (deadlineDefinitionId) => {
  try {
    const response = await httpRequest({
      method: 'PATCH',
      url: urlSubst(DEADLINE_API.ENABLE_DEFINITION, { deadlineDefinitionId }),
    })
    return { success: true, data: response }
  } catch (error) {
    return { success: false, message: error.message || 'Failed to enable deadline definition' }
  }
}

export const disableDeadlineDefinition = async (deadlineDefinitionId) => {
  try {
    const response = await httpRequest({
      method: 'PATCH',
      url: urlSubst(DEADLINE_API.DISABLE_DEFINITION, { deadlineDefinitionId }),
    })
    return { success: true, data: response }
  } catch (error) {
    return { success: false, message: error.message || 'Failed to disable deadline definition' }
  }
}

export const deleteDeadlineDefinition = async (deadlineDefinitionId) => {
  try {
    const response = await httpRequest({
      method: 'PATCH',
      url: urlSubst(DEADLINE_API.DELETE_DEFINITION, { deadlineDefinitionId }),
    })
    return { success: true, data: response }
  } catch (error) {
    return { success: false, message: error.message || 'Failed to delete deadline definition' }
  }
}

export function getProjectDeadlines (projectId, queryOptions = {}) {
  const queryKey = [API.GET_PROJECT_DEADLINES, projectId]
  const queryFn = async () => await httpRequest({
    baseURL: BASE_URL,
    url: urlSubst(API.GET_PROJECT_DEADLINES, { projectId }),
  })
  return useQuery({ queryKey, queryFn, ...queryOptions })
}

export function updateEndDate (mutationOptions = {}) {
  const mutationFn = async ({ deadlineId, endDate }) => await httpRequest({
    method: 'POST',
    baseURL: BASE_URL,
    url: urlSubst(API.UPDATE_DUE_DATE, { deadlineId }),
    data: {
      endDate,
    },
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function pauseDeadline (mutationOptions = {}) {
  const mutationFn = async ({ deadlineId }) => await httpRequest({
    method: 'PATCH',
    baseURL: BASE_URL,
    url: urlSubst(API.PAUSE_DEADLINE, { deadlineId }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function resumeDeadline (mutationOptions = {}) {
  const mutationFn = async ({ deadlineId }) => await httpRequest({
    method: 'PATCH',
    baseURL: BASE_URL,
    url: urlSubst(API.RESUME_DEADLINE, { deadlineId }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}
